import { FormControl, FormErrorMessage, Flex } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { forwardRef, useImperativeHandle } from "react";
import type { Variant } from "../../types/earmold.types";
import FileImageInput from "../FileImageInput";
import { useFile } from "../../hooks/file.hooks";
import { VariantType } from "../../types/variant.types";
import Select from "../inputs/Select";

type VariantFormProps = {
	value?: Variant;
	showImageInput?: boolean;
	onTypeChange?: (type: VariantType | undefined) => void;
};

export type VariantFormHandler = {
	values: () => Partial<Variant>;
	valid: () => Promise<boolean>;
};

const VariantForm = forwardRef<VariantFormHandler, VariantFormProps>(
	({ value, showImageInput, onTypeChange }: VariantFormProps, ref) => {
		const methods = useForm();
		const { data: file } = useFile({ id: value?.fileId });
		const typeRegister = methods.register("type", {
			required: "Ce champ est obligatoire",
		});

		useImperativeHandle(ref, () => ({
			valid: () => {
				return methods.trigger();
			},
			values: () => {
				const values = methods.getValues();

				return {
					label: values.label,
					description: values.description,
					options: {
						data: [],
						conditions: [],
					},
					file: values.file,
					type: values.type,
				};
			},
		}));
		return (
			<FormProvider {...methods}>
				<form style={{ marginTop: "32px", marginBottom: "32px" }}>
					<Flex direction="column" gap="24px">
						{showImageInput && (
							<FormControl
								isInvalid={!!methods.formState.errors.file}
								className="flex"
							>
								<div className="flex flex-col items-start">
									<FileImageInput
										defaultValue={file}
										onFileChange={(files: File[] | null) => {
											if (!files || files.length === 0) {
												methods.setValue("file", null);
												return;
											}

											methods.setValue("file", files[0]);
										}}
										{...methods.register("file")}
									></FileImageInput>
									<FormErrorMessage>
										<>{methods.formState.errors?.file?.message}</>
									</FormErrorMessage>
								</div>
							</FormControl>
						)}

						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Select
								defaultValue={value?.type}
								placeholder="Type de variant"
								{...typeRegister}
								onChange={(event) => {
									typeRegister.onChange(event);
									onTypeChange?.(event.target.value as VariantType | undefined);
								}}
							>
								<option value={VariantType.RADIO}>Bouton radio</option>
								<option value={VariantType.IMAGE_PER_ITEM}>
									Image par item
								</option>
								<option value={VariantType.CURSOR}>Curseur</option>
								<option value={VariantType.DROPDOWN}>Dropdown</option>
							</Select>
							<FormErrorMessage>
								<>{methods.formState.errors?.type?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Input
								id="label"
								placeholder="Nom"
								defaultValue={value?.label}
								{...methods.register("label", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.label?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.description}>
							<Input
								id="description"
								placeholder="Description"
								defaultValue={value?.description ?? ""}
								{...methods.register("description")}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.description?.message}</>
							</FormErrorMessage>
						</FormControl>
					</Flex>
				</form>
			</FormProvider>
		);
	},
);

export default VariantForm;
